import Swiper from 'swiper';

const videoCarousel = document.querySelector('.plarium-play__games-video-carousel .swiper');
const videoCarouselBase = 'plarium-play__games-video-carousel__slider';
const motionQuery = matchMedia('(prefers-reduced-motion)');

/**
 * Handle Video Controls
 *
 * @param {HTMLVideoElement} video - The video element
 * @param {string} action - The action to perform (play, pause, mute, unmute)
 * @param {HTMLElement} control - The control element to change
 *
 * @return {void}
 */
const handleVideoControl = (video, action, control) => {
  if (action === 'play') {
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          control.setAttribute('data-video-control', 'pause');
        })
        .catch(() => {});
    }
  } else if (action === 'pause') {
    // eslint-disable-next-line no-param-reassign -- Pause video
    video.pause();
    control.setAttribute('data-video-control', 'play');
  } else if (action === 'mute') {
    // eslint-disable-next-line no-param-reassign -- Mute video
    video.muted = true;
    control.setAttribute('data-video-control', 'unmute');
  } else if (action === 'unmute') {
    // eslint-disable-next-line no-param-reassign -- Unmute video
    video.muted = false;
    control.setAttribute('data-video-control', 'mute');
  }
};

/**
 * Deactivate all pagination items
 *
 * @param {NodeList} videoCarouselPagination - The pagination items
 *
 * @return {void}
 */
const deactivePaginationItems = (videoCarouselPagination) => {
  videoCarouselPagination.forEach((paginationItem) => {
    paginationItem.classList.remove(`${videoCarouselBase}__pagination__item--active`);
  });
};

/**
 * Stop all videos and mute them
 *
 * @param {NodeList} videoCarouselVideos - The video elements
 *
 * @return {void}
 */
const stopAllVideos = (videoCarouselVideos) => {
  videoCarouselVideos.forEach((video) => {
    const playPauseControl = video.parentElement.querySelector(
      `.${videoCarouselBase}__item__control:first-of-type`,
    );
    const muteUnmuteControl = video.parentElement.querySelector(
      `.${videoCarouselBase}__item__control:last-of-type`,
    );
    handleVideoControl(video, 'pause', playPauseControl);
    handleVideoControl(video, 'mute', muteUnmuteControl);
  });
};

/**
 * Initialize Video Carousel for the Plarium Play games
 */
const insitializeVideoCarousel = (videoCarouselElem) => {
  // Duplicate the slides in JS rather than in markup to avoid duplicate content. Needed to make the loop work correctly.
  const swiperWrapper = videoCarousel.querySelector('.swiper-wrapper');
  const sliderItems = swiperWrapper.innerHTML;
  swiperWrapper.innerHTML += sliderItems;

  const videoCarouselPagination = document.querySelectorAll(
    `.${videoCarouselBase}__pagination__item`,
  );
  const videoCarouselVideos = document.querySelectorAll(`.${videoCarouselBase}__item video`);
  const videoCarouselControls = document.querySelectorAll(`.${videoCarouselBase}__item__control`);

  const videoCarouselSwiper = new Swiper(videoCarouselElem, {
    loop: true,
    loopAddBlankSlides: false,
    centeredSlides: true,
    initialSlide: 1,
    keyboard: {
      enabled: true,
    },
    slidesPerView: 5,
    slidesPerGroup: 1,
    spaceBetween: 20,
    /**
     * Handle slide change event
     */
    onAny(eventName, ...args) {
      if (eventName === 'slideChange') {
        // Stop all videos.
        stopAllVideos(videoCarouselVideos);

        // Activate active slides video.
        const itemVideo = document.querySelector(
          `.${videoCarouselBase}__item[data-swiper-slide-index="${args[0].realIndex}"] video`,
        );

        if (itemVideo && itemVideo.paused && !motionQuery.matches) {
          const playPauseControl = itemVideo.parentElement.querySelector(
            `.${videoCarouselBase}__item__control:first-of-type`,
          );

          handleVideoControl(itemVideo, 'play', playPauseControl);
        }

        // Change active pagination item.
        deactivePaginationItems(videoCarouselPagination);
        videoCarouselPagination.forEach((paginationItem, index) => {
          if (index === args[0].realIndex || index === args[0].realIndex - 3) {
            paginationItem.classList.add(`${videoCarouselBase}__pagination__item--active`);
          }
        });
      }
    },
  });

  // Click event for pagination items.
  videoCarouselPagination.forEach((paginationItem, index) => {
    paginationItem.addEventListener('click', () => {
      videoCarouselSwiper.slideToLoop(index, 500);
    });
  });

  // Click event for video controls.
  videoCarouselControls.forEach((control) => {
    control.addEventListener('click', () => {
      const controlType = control.getAttribute('data-video-control');
      const video = control.parentElement.querySelector('video');

      handleVideoControl(video, controlType, control);
    });
  });
};

if (videoCarousel) {
  /**
   * Initialize the video carousel when it's in view.
   */
  const videoCarouselObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        insitializeVideoCarousel(videoCarousel);
        observer.disconnect();
      }
    });
  });

  const videoCarouselContainer = document.querySelector('.plarium-play__games-video-carousel');
  videoCarouselObserver.observe(videoCarouselContainer);
}
